import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Removes the free membership add-on when it is the only thing in the cart.
 *
 * snippets/oc-membership-guard.liquid renders this element ONLY when the cart
 * is in that state, so there is nothing to detect here — the element existing
 * is the signal. Because the drawer re-renders through the Section Rendering
 * API, a fresh instance is created whenever the cart still needs correcting,
 * which re-arms the guard without any polling or observers.
 *
 * Removal goes through the theme's own quantity event rather than a direct
 * /cart/change fetch: cart-items-component listens for
 * `quantity-selector:update`, treats quantity 0 as a line removal, and handles
 * the request, the section re-render, and the empty-cart transition. Firing the
 * event also means we don't depend on any of its internals staying public.
 *
 * The event listener checks `contains(event.target)`, so the event has to be
 * dispatched from inside the cart-items-component. This element is rendered
 * within it and bubbles, which satisfies that.
 */
class OCMembershipGuard extends HTMLElement {
  connectedCallback() {
    // One attempt per instance. If the removal fails the cart re-renders
    // unchanged, and a new instance takes the next attempt — retrying from
    // here would spin.
    if (this.dataset.fired === 'true') return;
    this.dataset.fired = 'true';

    const line = Number(this.dataset.line);
    if (!Number.isInteger(line) || line < 1) return;

    // Wait for the cart component to be upgraded and listening. During an SRA
    // re-render this element can connect before its ancestor is ready.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const cartItems = this.closest('cart-items-component');
      if (!cartItems) {
        console.warn('oc-membership-guard: no cart-items-component ancestor — add-on left in cart');
        return;
      }

      this.dispatchEvent(new QuantitySelectorUpdateEvent(0, line));
    });
  }
}

if (!customElements.get('oc-membership-guard')) {
  customElements.define('oc-membership-guard', OCMembershipGuard);
}
